import React, { useEffect, useState } from 'react';
import { normalizeSearch, popFrontURL } from '../../utils';

import { AccessorySingle } from './singles/AccessorySingle';
import { IAppliance } from '../../interfaces';
import LocalePrice from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/LocalePrice';
import { ProductSingle } from './singles/ProductSingle';
import { VentingSingle } from './singles/VentingSingle';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useProductPrice } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/store/queries/useProductsPriceAndInventories';

export default function SearchList(props: {
    searchString: string;
    allMagentoProducts: any[];
    isSearchListOpen: boolean;
    allProducts: any[];
    t: any;
    setSearchListOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
    const [products, setProducts]: [
        any,
        React.Dispatch<React.SetStateAction<any>>
    ] = useState([]);
    const [skus, setSkus]: [any, any] = useState([]);
    const [isAfterFetch, setAfterFetch] = useState(false);
    const { navigateTranslated, currentLocale } = usePageLocalization();
    const {
        allMagentoProducts,
        allProducts,
        isSearchListOpen,
        searchString,
        setSearchListOpen,
        t,
    } = props;
    useEffect(() => {
        if (searchString.length >= 2) {
            fetch(`/search?q=${normalizeSearch(searchString)}`)
                .then((response) => response.json())
                .then((data) => {
                    let searchResults = data?.body?.hits?.hits;
                    searchResults = searchResults?.filter((e: any) => {
                        const category =
                            e?._source?.category?.['en-CA']?.toLowerCase();
                        return [
                            'accessories',
                            'heaters',
                            'venting',
                            'parts',
                            'options',
                        ].some((str) => category.includes(str));
                    });
                    setSkus(
                        searchResults?.map(
                            (result: any) => result?._source?.code
                        )
                    );
                    setSearchListOpen(true);
                });
        } else {
            setSearchListOpen(false);
        }
    }, [searchString, setSearchListOpen, setSkus, setProducts]);

    useEffect(() => {
        const prodTmp = [];
        if (skus && skus.length > 0) {
            for (let i = 0; i < allProducts.length; i++) {
                if (skus.includes(allProducts[i].code)) {
                    if (
                        (currentLocale.includes('CA') &&
                            allProducts[
                                i
                            ]?.attributes?.available_country?.values.some(
                                (value: { [x: string]: string }) =>
                                    value[currentLocale] !== 'United States'
                            )) ||
                        (currentLocale.includes('US') &&
                            allProducts[
                                i
                            ]?.attributes?.available_country?.values.some(
                                (value: { [x: string]: string }) =>
                                    value[currentLocale] !== 'Canada'
                            )) ||
                        !allProducts[i]?.attributes?.available_country?.values
                    ) {
                        prodTmp.push(allProducts[i]);
                    }
                }
            }
        }
        setAfterFetch(true);
        setProducts(prodTmp);
    }, [allProducts, allMagentoProducts, skus, currentLocale]);

    if (isSearchListOpen) {
        return (
            <div className='search-dropdown'>
                {products.length > 0 ? (
                    <>
                        {products.map((product: any, index: number) => {
                            const category = getItemKey(
                                product?.slug?.en_CA,
                                null
                            )?.key;
                            if (
                                (category === 'accessories' ||
                                    category === 'options') &&
                                product?.code
                            ) {
                                if (product?.fields?.images) {
                                    return (
                                        <AccessorySingle
                                            key={index}
                                            product={product}
                                        />
                                    );
                                }
                                return undefined;
                            } else if (
                                category === 'heaters' &&
                                product?.code
                            ) {
                                return (
                                    <ProductSingle
                                        key={index}
                                        product={product}
                                        isDiscontinued={product?.slug?.[
                                            'en_CA'
                                        ]?.includes('discontinued')}
                                    />
                                );
                            } else if (
                                category === 'venting' &&
                                product?.code
                            ) {
                                return (
                                    <VentingSingle
                                        key={index}
                                        product={product}
                                    />
                                );
                            } else if (category === 'parts') {
                                return (
                                    <PartSingle
                                        key={`search-list-partsingle-${index}`}
                                        product={product}
                                    />
                                );
                            } else return undefined;
                        })}
                        {products.length > 15 && (
                            <div
                                className='see-more'
                                onClick={() =>
                                    navigateTranslated(
                                        `/search?q=${searchString}`
                                    )
                                }
                            >
                                {t('search.more')}
                            </div>
                        )}{' '}
                    </>
                ) : (
                    isAfterFetch && (
                        <p style={{ textAlign: 'start', margin: '0.8rem' }}>
                            {t('search.no-results')}
                        </p>
                    )
                )}
            </div>
        );
    } else {
        return <div style={{ display: 'none' }}></div>;
    }
}

function getItemKey(
    slugs: string | undefined,
    item: any
): { key: string; item: any } {
    if (!slugs) {
        return { key: 'NR', item };
    }
    if (slugs.includes('/cms/blog')) {
        return { key: 'cms', item };
    } else if (slugs.includes('/accessories/')) {
        return { key: 'accessories', item };
    } else if (slugs.includes('/archives/')) {
        return { key: 'archives', item };
    } else if (
        slugs.includes('/heaters/') ||
        slugs.includes('/discontinued/')
    ) {
        return { key: 'heaters', item };
    } else if (slugs.includes('/parts/')) {
        return { key: 'parts', item };
    } else if (slugs.includes('/venting/')) {
        return { key: 'venting', item };
    } else if (slugs.includes('/options/')) {
        return { key: 'options', item };
    } else {
        return { key: 'NR', item };
    }
}

function PartSingle(props: { product: IAppliance }) {
    const { product } = props;
    const { navigateTranslated, currentLocale } = usePageLocalization();
    const [price] = useProductPrice(product.code as string);

    const navigateToPartSingle = (product: any) => {
        if (product) {
            const link = popFrontURL(product?.slug?.[currentLocale], 1);
            if (link) {
                navigateTranslated(link);
            }
        }
    };

    return (
        <div
            className='product search-part'
            onClick={() => {
                navigateToPartSingle(product);
            }}
        >
            {/* <div className='product-image'>
            </div> */}
            <div className='product-info'>
                <span className='code'>{product.code}</span>
                <span className='title'>{product?.title?.[currentLocale]}</span>
                {price?.discount ? (
                    <div>
                        <span
                            className='price'
                            style={{
                                textDecoration: 'line-through',
                                marginRight: '10px',
                                color: '#b2aca2',
                            }}
                        >
                            {LocalePrice(
                                currentLocale,
                                price?.regularPrice.value
                            )}
                        </span>
                        <span className='price' style={{ color: '#d65e2a' }}>
                            {LocalePrice(
                                currentLocale,
                                price?.finalPrice.value
                            )}
                        </span>
                    </div>
                ) : (
                    <span className='price'>
                        {LocalePrice(currentLocale, price?.regularPrice.value)}
                    </span>
                )}
            </div>
        </div>
    );
}
